import { useQuery } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { GroupedResourcesResponse, Project, Resource, Task } from '@/types/types';
import { API_URL } from '@/lib/apiUrl';

export const PROJECT_RESOURCES_QUERY_KEY = 'project_resources';

const useResources = ({
  projectId,
  resourceableId,
  taskId,
  resourceableType,
}: {
  projectId?: Project['id'];
  resourceableId?: Resource['resourceable_id'];
  taskId?: Task['id'];
  resourceableType?: Resource['resourceable_type'];
}) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.resources.list({
      projectId,
      resourceableId,
      taskId,
      resourceableType,
    }).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Resource[]>(
        `${API_URL}/api/v1/resources`,
        {
          params: {
            project_id: projectId,
            resourceable_id: resourceableId,
            task_id: taskId,
            resourceable_type: resourceableType,
          },
        }
      );
      return data;
    },
  });
};

export default useResources;

function useGroupedResources({
  projectId,
  resourceableId,
  resourceableType,
  taskId,
  groupBy,
}: {
  projectId?: Project['id'];
  resourceableId?: Resource['resourceable_id'];
  resourceableType?: Resource['resourceable_type'];
  taskId?: Task['id'];
  groupBy: 'task_id' | 'company_account_category_id';
}) {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.resources.byGrouped(groupBy, {
      projectId,
      resourceableId,
      resourceableType,
      taskId,
    }).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<GroupedResourcesResponse[]>(
        `${API_URL}/api/v1/resources`,
        {
          params: {
            project_id: projectId,
            resourceable_id: resourceableId,
            task_id: taskId,
            resourceable_type: resourceableType,
            group_by: groupBy,
          },
        }
      );
      return data;
    },
  });
}

const useProjectResources = ({ projectId }: { projectId: Project['id'] }) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.resources.uniqueByProject({
      projectId,
    }).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Resource[]>(
        `${API_URL}/api/v1/resources/project_resources`,
        {
          params: {
            project_id: projectId,
          },
        }
      );
      return data;
    },
  });
};

export { useResources, useGroupedResources, useProjectResources };
